import { Bot, ClockAlert, Crown, Sparkles, UserX } from "lucide-react";

/** Config for membership badge (icon, colors, tooltip lines). */
export const MEMBERSHIP_STATUS_CONFIG = {
  premium: {
    Icon: Crown,
    iconClass: "text-amber-600 dark:text-amber-400",
    bgClass: "bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800",
    shortLabel: "Premium",
  },
  ai_plan: {
    Icon: Bot,
    iconClass: "text-blue-600 dark:text-blue-400",
    bgClass: "bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800",
    shortLabel: "AI Plan",
  },
  trial: {
    Icon: Sparkles,
    iconClass: "text-violet-600 dark:text-violet-400",
    bgClass: "bg-violet-50 border-violet-200 dark:bg-violet-950/40 dark:border-violet-800",
    shortLabel: "Trial",
  },
  trial_expired: {
    Icon: ClockAlert,
    iconClass: "text-orange-600 dark:text-orange-400",
    bgClass: "bg-orange-50 border-orange-200 dark:bg-orange-950/40 dark:border-orange-800",
    shortLabel: "Trial ended",
  },
  premium_expired: {
    Icon: Crown,
    iconClass: "text-muted-foreground",
    bgClass: "bg-muted border-border",
    shortLabel: "Premium expired",
  },
  ai_plan_expired: {
    Icon: Bot,
    iconClass: "text-muted-foreground",
    bgClass: "bg-muted border-border",
    shortLabel: "AI expired",
  },
  unpaid: {
    Icon: UserX,
    iconClass: "text-red-600 dark:text-red-400",
    bgClass: "bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800",
    shortLabel: "Unpaid",
  },
};

export function getMembershipConfig(customer) {
  const key = customer?.membership_status || "unpaid";
  return MEMBERSHIP_STATUS_CONFIG[key] || MEMBERSHIP_STATUS_CONFIG.unpaid;
}

/** Tooltip lines for hover detail. */
export function getMembershipTooltipLines(customer) {
  const lines = [];
  const label = customer?.membership_label;
  if (label) lines.push(label);

  if (customer?.on_trial) {
    lines.push(`On trial: Yes`);
    const left = customer.trial_days_left;
    if (left != null) {
      lines.push(
        left === 0
          ? "Trial ends today"
          : `${left} day${left === 1 ? "" : "s"} remaining`,
      );
    }
    if (customer.trial_ends_at) {
      const d = new Date(customer.trial_ends_at);
      if (!Number.isNaN(d.getTime())) {
        lines.push(`Ends: ${d.toLocaleString()}`);
      }
    }
  } else if (customer?.membership_status === "trial_expired") {
    lines.push("On trial: No (expired)");
    if (customer.trial_ends_at) {
      const d = new Date(customer.trial_ends_at);
      if (!Number.isNaN(d.getTime())) {
        lines.push(`Trial ended: ${d.toLocaleDateString()}`);
      }
    }
  } else {
    lines.push(`On trial: No`);
  }

  if (Number(customer?.premium_member) === 1) {
    const exp = customer.expiry_date
      ? new Date(customer.expiry_date).toLocaleDateString()
      : "No expiry";
    lines.push(`Premium: Yes (until ${exp})`);
  } else {
    lines.push("Premium: No");
  }

  if (Number(customer?.ai_plan) === 1) {
    const exp = customer.ai_plan_expiry
      ? new Date(customer.ai_plan_expiry).toLocaleDateString()
      : "-";
    lines.push(`AI plan flag: Yes (expiry ${exp})`);
  } else {
    lines.push("AI plan flag: No");
  }

  return lines;
}

/** Client-side fallback when API fields missing (older backend). */
export function deriveMembershipFromUser(c) {
  if (c?.membership_status) return c;
  const now = Date.now();
  let membership_status = "unpaid";
  let membership_label = "Unpaid / no plan";
  let on_trial = false;
  let trial_days_left = null;
  let trial_ends_at = c?.ai_plan_expiry || null;

  if (Number(c?.premium_member) === 1) {
    const exp = c.expiry_date ? new Date(c.expiry_date).getTime() : Infinity;
    if (exp > now) {
      membership_status = "premium";
      membership_label = "Premium (active)";
    } else if (c.expiry_date) {
      membership_status = "premium_expired";
      membership_label = "Premium (expired)";
    }
  } else if (
    Number(c?.ai_plan) === 1 &&
    Number(c?.trial) !== 1 &&
    c?.ai_plan_expiry &&
    new Date(c.ai_plan_expiry).getTime() > now
  ) {
    membership_status = "ai_plan";
    membership_label = "AI plan (active)";
  } else if (Number(c?.trial) === 1) {
    on_trial = true;
    membership_status = "trial";
    const end = c.ai_plan_expiry ? new Date(c.ai_plan_expiry).getTime() : null;
    if (end && end > now) {
      trial_days_left = Math.ceil((end - now) / 86400000);
      membership_label = `Trial (${trial_days_left} day${trial_days_left === 1 ? "" : "s"} left)`;
    } else {
      membership_label = "Trial (active)";
    }
  } else if (
    c?.ai_plan_expiry &&
    Number(c?.ai_plan) !== 1 &&
    new Date(c.ai_plan_expiry).getTime() <= now
  ) {
    membership_status = "trial_expired";
    membership_label = "Trial expired";
    trial_days_left = 0;
  }

  return {
    ...c,
    membership_status,
    membership_label,
    on_trial,
    trial_days_left,
    trial_ends_at: trial_ends_at
      ? typeof trial_ends_at === "string"
        ? trial_ends_at
        : new Date(trial_ends_at).toISOString()
      : null,
  };
}
